import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Toaster, toast } from 'sonner';
import { 
  BookOpen, 
  Sun, 
  Moon, 
  LogOut, 
  Search, 
  Filter, 
  Package, 
  TrendingUp,
  Bell,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Truck,
  BarChart3,
  Settings,
  X
} from 'lucide-react';
import { formatCurrency, formatRank, getStatusConfig, getCountryFlag, debounce } from '../lib/utils';
import { ProfitTooltip } from '../components/ProfitTooltip';
import { CreateAcquisitionModal } from '../components/CreateAcquisitionModal';
import { CreateAlertModal } from '../components/CreateAlertModal';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Dashboard() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  // State
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [search, setSearch] = useState('');
  const [minProfit, setMinProfit] = useState('1');
  const [maxRank, setMaxRank] = useState('');
  const [supplierCountry, setSupplierCountry] = useState('');
  const [status, setStatus] = useState('ALL');
  const [showFilters, setShowFilters] = useState(false);
  
  // Modals
  const [selectedBook, setSelectedBook] = useState(null);
  const [showAcquisitionModal, setShowAcquisitionModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [hoveredBook, setHoveredBook] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Fetch books
  const fetchBooks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '25',
      });
      
      if (search) params.append('search', search);
      if (minProfit) params.append('min_profit', minProfit);
      if (maxRank) params.append('max_rank', maxRank);
      if (supplierCountry) params.append('supplier_country', supplierCountry);
      if (status && status !== 'ALL') params.append('status', status);
      
      const response = await fetch(`${API_URL}/api/books?${params}`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setBooks(data.books);
        setTotalPages(data.total_pages);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Failed to fetch books:', error);
      toast.error('Failed to load books');
    } finally {
      setLoading(false);
    }
  }, [page, search, minProfit, maxRank, supplierCountry, status]);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/dashboard`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  }, []);

  // Seed mock data
  const seedMockData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/seed-mock-data`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('Mock data seeded successfully');
        fetchBooks();
        fetchAnalytics();
      }
    } catch (error) {
      toast.error('Failed to seed mock data');
    }
  };

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearch(value);
      setPage(1);
    }, 300),
    []
  );

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Handle row hover for tooltip
  const handleRowHover = (book, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.right + 10,
      y: rect.top,
    });
    setHoveredBook(book);
  };

  const handleRowLeave = () => {
    setHoveredBook(null);
  };

  // Actions
  const handleCreateAcquisition = (book) => {
    setSelectedBook(book);
    setShowAcquisitionModal(true);
  };

  const handleCreateAlert = (book) => {
    setSelectedBook(book);
    setShowAlertModal(true);
  };

  const applyFilters = () => {
    setPage(1);
    fetchBooks();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setSearch('');
    setMinProfit('1');
    setMaxRank('');
    setSupplierCountry('');
    setStatus('ALL');
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="h-7 w-7 text-gold" />
            <span className="font-heading text-lg font-bold hidden sm:inline">Book Arbitrage Pro</span>
          </div>
          
          <nav className="flex items-center gap-2">
            <Link to="/shipments">
              <Button variant="ghost" size="sm" className="gap-2" data-testid="nav-shipments">
                <Truck className="h-4 w-4" />
                <span className="hidden sm:inline">Shipments</span>
              </Button>
            </Link>
            <Link to="/alerts">
              <Button variant="ghost" size="sm" className="gap-2" data-testid="nav-alerts">
                <Bell className="h-4 w-4" />
                <span className="hidden sm:inline">Alerts</span>
              </Button>
            </Link>
            <Link to="/analytics">
              <Button variant="ghost" size="sm" className="gap-2" data-testid="nav-analytics">
                <BarChart3 className="h-4 w-4" />
                <span className="hidden sm:inline">Analytics</span>
              </Button>
            </Link>
          </nav>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="dashboard-theme-toggle"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu">
                  <div className="h-8 w-8 rounded-full bg-gold/20 flex items-center justify-center">
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
                    ) : (
                      <span className="text-sm font-medium text-gold">
                        {user?.name?.charAt(0) || 'U'}
                      </span>
                    )}
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem className="gap-2">
                  <Settings className="h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem className="gap-2 text-destructive" onClick={logout} data-testid="logout-button">
                  <LogOut className="h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {/* Analytics Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Total Books</div>
              <div className="text-2xl font-bold font-mono">{total.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">FBA Inventory</div>
              <div className="text-2xl font-bold font-mono text-profit">
                {analytics?.fba_inventory || 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Expected Profit</div>
              <div className="text-2xl font-bold font-mono text-profit">
                {formatCurrency(analytics?.fba_expected_profit || 0)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Realized Profit</div>
              <div className="text-2xl font-bold font-mono text-gold">
                {formatCurrency(analytics?.realized_profit || 0)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Active Alerts</div>
              <div className="text-2xl font-bold font-mono">{analytics?.active_alerts || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by ISBN or title..."
                  className="pl-10"
                  onChange={(e) => debouncedSearch(e.target.value)}
                  data-testid="search-input"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="gap-2"
                  data-testid="filter-toggle"
                >
                  <Filter className="h-4 w-4" />
                  Filters
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { fetchBooks(); fetchAnalytics(); }}
                  className="gap-2"
                  data-testid="refresh-button"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <Button
                  onClick={seedMockData}
                  className="gap-2 bg-gold/10 text-gold hover:bg-gold/20"
                  data-testid="seed-data-button"
                >
                  <Plus className="h-4 w-4" />
                  Seed Data
                </Button>
              </div>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-5 gap-4 animate-slide-up">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Min Profit (£)</label>
                  <Input
                    type="number"
                    value={minProfit}
                    onChange={(e) => setMinProfit(e.target.value)}
                    placeholder="1.00"
                    data-testid="filter-min-profit"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Max Rank</label>
                  <Input
                    type="number"
                    value={maxRank}
                    onChange={(e) => setMaxRank(e.target.value)}
                    placeholder="500000"
                    data-testid="filter-max-rank"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Supplier Country</label>
                  <Select value={supplierCountry || "ALL_COUNTRIES"} onValueChange={(val) => setSupplierCountry(val === "ALL_COUNTRIES" ? "" : val)}>
                    <SelectTrigger data-testid="filter-country">
                      <SelectValue placeholder="All Countries" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL_COUNTRIES">All Countries</SelectItem>
                      <SelectItem value="GB">🇬🇧 UK Only</SelectItem>
                      <SelectItem value="US">🇺🇸 US</SelectItem>
                      <SelectItem value="DE">🇩🇪 Germany</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Status</label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid="filter-status">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All Status</SelectItem>
                      <SelectItem value="AVAILABLE">Available</SelectItem>
                      <SelectItem value="SOURCED">Sourced</SelectItem>
                      <SelectItem value="PREPPED">Prepped</SelectItem>
                      <SelectItem value="LIVE">Live</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Button onClick={applyFilters} className="flex-1" data-testid="apply-filters">
                    Apply
                  </Button>
                  <Button variant="ghost" onClick={clearFilters} size="icon">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm font-medium">
              Book Inventory ({total.toLocaleString()} results)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full dense-table" data-testid="books-table">
                <thead className="sticky-header border-b">
                  <tr>
                    <th className="text-left">ISBN</th>
                    <th className="text-left">Title</th>
                    <th className="text-left">Source</th>
                    <th className="text-right">Used £</th>
                    <th className="text-right">Amazon £</th>
                    <th className="text-right">Rank</th>
                    <th className="text-center">Country</th>
                    <th className="text-right">Est. Profit</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={10} className="text-center py-8 text-muted-foreground">
                        Loading...
                      </td>
                    </tr>
                  ) : books.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center py-8 text-muted-foreground">
                        No books found. Click "Seed Data" to add sample books.
                      </td>
                    </tr>
                  ) : (
                    books.map((book) => {
                      const statusConfig = getStatusConfig(book.status);
                      const profit = book.expected_profit || 0;
                      const isProfitable = profit >= 1;
                      
                      return (
                        <tr
                          key={book.book_id}
                          className="border-b hover:bg-muted/50 transition-colors cursor-pointer"
                          onMouseEnter={(e) => handleRowHover(book, e)}
                          onMouseLeave={handleRowLeave}
                          data-testid={`book-row-${book.book_id}`}
                        >
                          <td className="font-mono text-xs">{book.isbn}</td>
                          <td className="max-w-[200px] truncate" title={book.title}>
                            {book.title}
                          </td>
                          <td className="text-muted-foreground text-xs">{book.source}</td>
                          <td className="text-right font-mono">
                            {formatCurrency(book.used_price)}
                          </td>
                          <td className="text-right font-mono">
                            {formatCurrency(book.amazon_price)}
                          </td>
                          <td className="text-right font-mono text-muted-foreground">
                            {formatRank(book.amazon_rank)}
                          </td>
                          <td className="text-center">
                            <span title={book.seller_country}>
                              {getCountryFlag(book.seller_country)}
                            </span>
                          </td>
                          <td className={`text-right font-mono font-medium ${isProfitable ? 'text-profit' : 'text-loss'}`}>
                            {formatCurrency(profit)}
                          </td>
                          <td className="text-center">
                            <Badge variant="outline" className={`text-xs ${statusConfig.className}`}>
                              {statusConfig.label}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`actions-${book.book_id}`}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleCreateAcquisition(book)} className="gap-2">
                                  <Package className="h-4 w-4" />
                                  Create Acquisition
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleCreateAlert(book)} className="gap-2">
                                  <Bell className="h-4 w-4" />
                                  Create Alert
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="prev-page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  data-testid="next-page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Profit Tooltip */}
      {hoveredBook && (
        <ProfitTooltip
          book={hoveredBook}
          position={tooltipPosition}
        />
      )}

      {/* Modals */}
      <CreateAcquisitionModal
        open={showAcquisitionModal}
        onClose={() => setShowAcquisitionModal(false)}
        book={selectedBook}
        onSuccess={() => {
          fetchBooks();
          fetchAnalytics();
          toast.success('Acquisition created successfully');
        }}
      />

      <CreateAlertModal
        open={showAlertModal}
        onClose={() => setShowAlertModal(false)}
        book={selectedBook}
        onSuccess={() => {
          fetchAnalytics();
          toast.success('Alert created successfully');
        }}
      />
    </div>
  );
}
